import React, { useState, useEffect } from 'react';
import { getPredictionsAnalytics } from '../../services/AnalyticsService';
import { getArtistId } from '../../state/ArtistManager.js';

const PredictionsPanel = () => {
  const [predictionsData, setPredictionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const artistId = getArtistId();

  useEffect(() => {
    loadPredictionsAnalytics();
  }, [artistId]);

  const loadPredictionsAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getPredictionsAnalytics(artistId);
      setPredictionsData(data);
    } catch (error) {
      console.error('Error loading predictions analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4">Loading AI predictions...</div>;

  const { viralMetrics, recommendations, timing, forecasts } = predictionsData || {};

  return (
    <div className="space-y-6">
      {/* Viral Metrics */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">🤖 AI Viral Predictions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold">{viralMetrics?.highPotential || 0}</div>
            <div className="text-sm opacity-90">High Potential</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{viralMetrics?.readyTracks || 0}</div>
            <div className="text-sm opacity-90">Ready Tracks</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{viralMetrics?.avgScore || 0}</div>
            <div className="text-sm opacity-90">Avg Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{viralMetrics?.playlistPotential || 0}</div>
            <div className="text-sm opacity-90">Playlist Ready</div>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">💡 AI Recommendations</h3>
        <div className="space-y-3">
          {recommendations?.map((rec, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
              <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>
              <div className="flex-1 text-gray-800">{rec}</div>
            </div>
          )) || <div className="text-gray-500">No recommendations available</div>}
        </div>
      </div>

      {/* Timing Insights */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">⏰ Optimal Timing Insights</h3>
        <div className="grid md:grid-cols-3 gap-6">
          
          {/* Posting Times */}
          <div>
            <h4 className="font-semibold mb-3 text-blue-600">Best Posting Times</h4>
            <div className="space-y-2">
              {Object.entries(timing?.optimalPostingTimes || {}).map(([time, count]) => (
                <div key={time} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                  <span className="font-medium">{time}</span>
                  <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs">
                    {count} tracks
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Seasonal Trends */}
          <div>
            <h4 className="font-semibold mb-3 text-green-600">Seasonal Trends</h4>
            <div className="space-y-2">
              {timing?.seasonalTrends?.map((trend, index) => (
                <div key={index} className="p-2 bg-green-50 rounded">
                  <div className="font-medium text-sm">{trend.season}</div>
                  <div className="text-xs text-gray-600">{trend.trend}</div>
                  <div className="text-xs text-green-600 font-semibold">
                    {(trend.potential * 100).toFixed(0)}% potential
                  </div>
                </div>
              )) || <div className="text-gray-500">No seasonal data</div>}
            </div>
          </div>

          {/* Platform Optimization */}
          <div>
            <h4 className="font-semibold mb-3 text-purple-600">Platform Optimization</h4>
            <div className="space-y-2">
              {Object.entries(timing?.platformOptimization || {}).map(([platform, data]) => (
                <div key={platform} className="p-2 bg-purple-50 rounded">
                  <div className="font-medium text-sm capitalize">{platform}</div>
                  <div className="text-xs text-gray-600">{data.optimalLength}</div>
                  <div className="text-xs text-purple-600 font-semibold">
                    {data.bestTracks} suitable tracks
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Growth Forecasts */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">📈 Growth Forecasts</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Week</th>
                <th className="text-left p-2">Predicted Streams</th>
                <th className="text-left p-2">Confidence</th>
                <th className="text-left p-2">Trend</th>
              </tr>
            </thead>
            <tbody>
              {forecasts?.slice(0, 6).map((forecast, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="p-2 font-medium">{forecast.week}</td>
                  <td className="p-2">{forecast.predictedStreams?.toLocaleString() || 0}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      parseFloat(forecast.confidence) > 0.8 ? 'bg-green-100 text-green-800' : 
                      parseFloat(forecast.confidence) > 0.6 ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-red-100 text-red-800'
                    }`}>
                      {(parseFloat(forecast.confidence) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      forecast.trendDirection === 'up' ? 'bg-green-500' : 
                      forecast.trendDirection === 'down' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}></span>
                    <span className="ml-2 capitalize">{forecast.trendDirection}</span>
                  </td>
                </tr>
              )) || <tr><td colSpan="4" className="p-4 text-gray-500 text-center">No forecast data available</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PredictionsPanel;
